import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";

import { Modal, Input, Textarea, Button } from "../ui";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const schema = yup.object().shape({
  shortcut: yup
    .string()
    .min(2, "Atalho muito curto")
    .max(15, "Atalho muito longo")
    .required("Atalho é obrigatório"),
  message: yup
    .string()
    .min(8, "Mensagem muito curta")
    .max(30000, "Mensagem muito longa")
    .required("Mensagem é obrigatória"),
});

const QuickAnswersModal = ({
  open,
  onClose,
  quickAnswerId,
  initialValues,
  onSave,
}) => {
  const isMounted = useRef(true);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      shortcut: "",
      message: "",
    },
  });

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchQuickAnswer = async () => {
      if (initialValues) {
        reset({
          shortcut: initialValues.shortcut || "",
          message: initialValues.message || "",
        });
        return;
      }

      if (!quickAnswerId) {
        reset({
          shortcut: "",
          message: "",
        });
        return;
      }

      try {
        const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
        if (isMounted.current) {
          reset({
            shortcut: data.shortcut || "",
            message: data.message || "",
          });
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      fetchQuickAnswer();
    }
  }, [quickAnswerId, open, initialValues, reset]);

  const handleClose = () => {
    reset({
      shortcut: "",
      message: "",
    });
    onClose();
  };

  const handleSaveQuickAnswer = async (data) => {
    setLoading(true);
    try {
      if (quickAnswerId) {
        await api.put(`/quickAnswers/${quickAnswerId}`, data);
        handleClose();
      } else {
        const { data: newQuickAnswer } = await api.post("/quickAnswers", data);
        if (onSave) {
          onSave(newQuickAnswer);
        }
        handleClose();
      }
      toast.success(i18n.t("quickAnswersModal.success"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        quickAnswerId
          ? i18n.t("quickAnswersModal.title.edit")
          : i18n.t("quickAnswersModal.title.add")
      }
      size="md"
      footer={
        <>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {i18n.t("quickAnswersModal.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            form="quick-answer-form"
            loading={loading}
            disabled={loading}
          >
            {quickAnswerId
              ? i18n.t("quickAnswersModal.buttons.okEdit")
              : i18n.t("quickAnswersModal.buttons.okAdd")}
          </Button>
        </>
      }
    >
      <form
        id="quick-answer-form"
        onSubmit={handleSubmit(handleSaveQuickAnswer)}
        className="space-y-4"
      >
        <Input
          label={i18n.t("quickAnswersModal.form.shortcut")}
          {...register("shortcut")}
          error={errors.shortcut?.message}
          autoFocus
          placeholder="Ex: /saudacao"
        />

        <Textarea
          label={i18n.t("quickAnswersModal.form.message")}
          {...register("message")}
          error={errors.message?.message}
          rows={5}
          resize="vertical"
          placeholder="Digite a mensagem de resposta rápida..."
        />
      </form>
    </Modal>
  );
};

export default QuickAnswersModal;
